import {
  Card,
  CardContent,
  CardHeader,
  Chip,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import React from "react";
import { booleanToDisplay } from "utils/displayUtils";
import { ASSESSMENT_TYPE_LABELS, CLINICAL_DOCUMENT_TYPE_LABELS } from "./constants";
import { theme as appTheme } from "@answersnow/pkg-theme";

// @ts-expect-error - pkg-theme typings will be addressed in a future type iteration
const { purple, orange, grey } = appTheme.palette;

interface DocumentRuleCardProps {
  rule: any;
  action?: React.ReactNode;
}

export default function DocumentRuleCard({ rule, action }: DocumentRuleCardProps) {
  const theme = useTheme();

  return (
    <Card
      variant="outlined"
      sx={{ border: "1px solid", borderColor: "divider", backgroundColor: "background.paper" }}
    >
      <CardHeader
        title={
          CLINICAL_DOCUMENT_TYPE_LABELS[rule.clinical_document_type] ??
          rule.clinical_document_type
        }
        titleTypographyProps={{ variant: "h5" }}
        action={action}
      />
      <CardContent sx={{ pt: 0, px: "16px", pb: "16px", "&:last-child": { pb: "16px" } }}>
        <Stack gap={3}>
          {rule.required_assessments && (
            <Stack gap={1}>
              <Typography variant="caption" color={grey[600]}>
                Assessment requirements
              </Typography>
              <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
                {rule.required_assessments.assessment_types?.map(
                  (type: string, index: number) => (
                    <React.Fragment key={type}>
                      <Typography variant="body2">
                        {ASSESSMENT_TYPE_LABELS[type] ?? type}
                      </Typography>
                      {index <
                        rule.required_assessments.assessment_types.length - 1 && (
                        <Chip
                          label={rule.required_assessments.operator}
                          size="small"
                          sx={{
                            fontWeight: 400,
                            fontSize: "12px",
                            lineHeight: 1.4,
                            borderRadius: "3px",
                            ...(rule.required_assessments.operator === "AND" && {
                              backgroundColor: purple[100],
                              color: theme.palette.primary.main,
                            }),
                            ...(rule.required_assessments.operator === "OR" && {
                              backgroundColor: orange[100],
                              color: theme.palette.warning.main,
                            }),
                          }}
                        />
                      )}
                    </React.Fragment>
                  )
                )}
              </Stack>
            </Stack>
          )}
          <Stack gap={1}>
            <Typography variant="caption" color={grey[600]}>
              Requires referring provider
            </Typography>
            <Typography variant="body2">
              {booleanToDisplay(rule.requires_referring_provider)}
            </Typography>
          </Stack>
          <Stack gap={1}>
            <Typography variant="caption" color={grey[600]}>
              Requires criminal and legal history reported
            </Typography>
            <Typography variant="body2">
              {booleanToDisplay(rule.requires_criminal_legal_history)}
            </Typography>
          </Stack>
          <Stack gap={1}>
            <Typography variant="caption" color={grey[600]}>
              Requires drug or alcohol use reported
            </Typography>
            <Typography variant="body2">
              {booleanToDisplay(rule.requires_drug_alcohol_use)}
            </Typography>
          </Stack>
          <Stack gap={1}>
            <Typography variant="caption" color={grey[600]}>
              Requires trauma history reported
            </Typography>
            <Typography variant="body2">
              {booleanToDisplay(rule.requires_trauma_history)}
            </Typography>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}
